<script setup lang="ts">
import { AsYouType, getCountries, getCountryCallingCode, isValidPhoneNumber, parsePhoneNumberFromString, type CountryCode } from 'libphonenumber-js/max'
import { onMounted, ref, watch } from 'vue'

import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { formatPhoneNumber } from '@/helpers/format-text'
import { cn } from '@/lib/utils'
import { Check, ChevronsUpDown, Phone } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'

const props = withDefaults(
    defineProps<{
        placeholder?: string
        defaultCallingCode?: CountryCode
        id?: string
    }>(),
    {
        id: 'phone'
    }
)

const selectedCountry = ref<CountryCode | null>(null)
const phoneNumber = defineModel<string>({
    default: ``,
})
const formattedNumber = ref('')
const isValid = ref(false)

const open = ref(false)
const countryRef = ref('')

const { locale } = useI18n()

const countries = getCountries()
    .map((country) => ({
        id: country,
        callingCode: getCountryCallingCode(country),
        label: new Intl.DisplayNames([locale.value], { type: 'region' }).of(country) || country,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))

function countryCodeToFlag(countryCode: string) {
    if (!countryCode || countryCode.length !== 2 || !/^[a-zA-Z]+$/.test(countryCode)) {
        return '🏳️'
    }

    const code = countryCode.toUpperCase()
    const offset = 127397

    const flag = Array.from(code)
        .map((letter) => String.fromCodePoint(letter.charCodeAt(0) + offset))
        .join('')

    return flag
}

watch(
    phoneNumber,
    (newValue) => {
    if (newValue && !newValue.startsWith('+')) {
        phoneNumber.value = '+' + newValue
        return
    }

    if (newValue.startsWith('+')) {
        const parsed = parsePhoneNumberFromString(newValue)
        if (parsed?.country) {
            selectedCountry.value = parsed.country
        }
    }

    if (selectedCountry.value) {
        countryRef.value = selectedCountry.value
        const formatter = new AsYouType(selectedCountry.value)
        formattedNumber.value = formatter.input(newValue)
        phoneNumber.value = formattedNumber.value
        isValid.value = isValidPhoneNumber(newValue, selectedCountry.value)
    }
    },
    { immediate: true },
)

function onCountrySelect(newCountry: CountryCode) {
    selectedCountry.value = newCountry
    countryRef.value = newCountry

    const prefix = `+${getCountryCallingCode(newCountry)}`
    const nationalNumber = phoneNumber.value.replace(/^\+?\d*/, '')

    const formatter = new AsYouType(newCountry)
    const withPrefix = prefix + nationalNumber
    const formatted = formatter.input(withPrefix)

    phoneNumber.value = formatted
    formattedNumber.value = formatted
    isValid.value = isValidPhoneNumber(formatted, newCountry)
}

onMounted(() => {
    const defaultCallingCode = props.defaultCallingCode ?? (locale.value?.toLowerCase() === 'pt-br' ? 'BR' : null)

    // If we already detected the country from the incoming `value` prefix, don't override it.
    if (selectedCountry.value) return

    if (defaultCallingCode) {
        const formatter = new AsYouType(defaultCallingCode)

        selectedCountry.value = defaultCallingCode
        countryRef.value = defaultCallingCode

        if (phoneNumber.value) {
            phoneNumber.value = formatter.input(phoneNumber.value)
            return
        }

        phoneNumber.value = `+${getCountryCallingCode(defaultCallingCode)}`
    }
})
</script>

<template>
    <div class="space-y-2" dir="ltr">
        <div class="flex rounded-lg shadow-xs shadow-black/[.04]">
            <Popover v-model:open="open">
                <PopoverTrigger as-child>
                    <button
                        role="combobox"
                        :aria-expanded="open"
                        class="border-input bg-background text-muted-foreground ring-offset-background focus-within:border-primary focus-within:text-foreground focus-within:ring-primary/50 hover:bg-accent hover:text-foreground relative inline-flex items-center self-stretch rounded-l-lg border py-2 ps-3 pe-2 transition-shadow focus-within:z-10 focus-within:ring-[3px] focus-within:outline-hidden has-disabled:pointer-events-none has-disabled:opacity-50"
                    >
                        <Phone v-if="!selectedCountry" :size="16" aria-hidden="true" />
                        <span v-else class="text-xl">
                            {{ countryCodeToFlag(selectedCountry) }}
                        </span>
                        <ChevronsUpDown class="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </button>
                </PopoverTrigger>
                <PopoverContent align="start" class="w-[280px] p-0">
                    <Command v-model="countryRef">
                        <CommandInput :placeholder="$t('Search country')" />
                        <CommandEmpty>{{ $t('Country not found') }}</CommandEmpty>
                        <CommandList>
                            <CommandGroup>
                                <CommandItem
                                    v-for="country in countries"
                                    :key="country.label"
                                    :value="country.id"
                                    @select="
                                        () => {
                                            open = false
                                            onCountrySelect(country.id)
                                        }
                                    "
                                    class="text-sm"
                                >
                                    <Check :class="cn('mr-2 h-4 w-4', countryRef === country.id ? 'opacity-100' : 'opacity-0')" />
                                    {{ countryCodeToFlag(country.id) }} {{ country.label }} (+{{ country.callingCode }})
                                </CommandItem>
                            </CommandGroup>
                        </CommandList>
                    </Command>
                </PopoverContent>
            </Popover>

            <input
                :id="id"
                v-model="phoneNumber"
                type="tel"
                :maxlength="selectedCountry === 'BR' ? 17 : undefined"
                :placeholder="$t(placeholder ?? '')"
                @input="(e: Event) => (phoneNumber = formatPhoneNumber((e.target as HTMLInputElement).value))"
                class="border-input bg-background text-foreground ring-offset-background placeholder:text-muted-foreground/70 focus-visible:border-primary focus-visible:ring-primary/50 -ml-px flex h-12 w-full rounded-lg rounded-l-none border px-3 py-2 text-sm shadow-none shadow-black/[.04] transition-shadow focus-visible:z-10 focus-visible:ring-[3px] focus-visible:outline-hidden disabled:cursor-not-allowed disabled:opacity-50"
                :data-invalid-number="phoneNumber && !isValid"
            />
        </div>
    </div>
</template>
